import { useState, type FormEvent, type ReactNode } from 'react';
import {
  Briefcase,
  Sparkles,
  Search,
  Info,
  Lightbulb,
  Ban,
  ShieldAlert,
  Phone,
  CalendarClock,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useDealCoach } from '../hooks/useCopilots';
import { AISuggestionBadge, AI_SUGGESTION_RING } from './AISuggestionBadge';
import { impactTone, formatImpact, formatCurrency, formatCount, formatDate, DEPARTMENT_PERSONA } from '../constants';
import type { DealFactor } from '../types';
import { useUser } from '@/shared/hooks/useAuth';

export function DealCoachPanel({ enabled = true }: { enabled?: boolean }) {
  const [input, setInput] = useState('');
  const [dealId, setDealId] = useState('');

  const { data, isLoading, isError, error, isFetching } = useDealCoach({
    dealId,
    enabled,
  });

  const user = useUser();
  const dept = user?.department || 'sales';
  const persona = DEPARTMENT_PERSONA[dept as keyof typeof DEPARTMENT_PERSONA] || DEPARTMENT_PERSONA['sales'];

  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.error;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDealId(input.trim());
  }

  const factorColumns: Column<DealFactor>[] = [
    {
      key: 'label',
      header: 'Factor',
      render: (f) => <span className="font-medium text-on-surface">{f.label}</span>,
    },
    {
      key: 'value',
      header: 'Value',
      render: (f) => <span className="text-on-surface-variant">{String(f.value)}</span>,
    },
    {
      key: 'impact',
      header: 'Impact on score',
      align: 'right',
      render: (f) => (
        <Badge tone={impactTone(f.impact)}>
          {formatImpact(f.impact)} pts
        </Badge>
      ),
    },
  ];

  return (
    <Card className={data?.ok ? AI_SUGGESTION_RING : undefined}>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" /> {persona.coachEntity} Coach
            {data?.ok && <AISuggestionBadge />}
          </span>
        }
        subtitle={`Enter a ${persona.coachEntity.toLowerCase()} id to get its win-probability coaching and next-best-action, grounded in the ${persona.coachEntity.toLowerCase()}'s real fields.`}
      />
      <CardBody className="space-y-6">
        {/* Deal id lookup */}
        <form onSubmit={onSubmit} className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`${persona.coachEntity} id`}
            aria-label={`${persona.coachEntity} id`}
            className="h-10 min-w-[16rem] flex-1 rounded border border-outline bg-surface px-3 text-data-md text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
          <Button type="submit" disabled={!input.trim()} loading={isFetching && !!dealId}>
            <Search className="h-4 w-4" />
            Coach this {persona.coachEntity.toLowerCase()}
          </Button>
        </form>

        {/* Provenance note */}
        <div className="flex items-start gap-2 rounded-card border border-info/40 bg-info/5 p-3 text-body-sm text-on-surface-variant">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-info-text" />
          <p>
            Coaching is an <span className="font-medium text-on-surface">AI Suggestion</span>. The score
            and factors come from the scoring model; every value below is echoed from this {persona.coachEntity.toLowerCase()}&apos;s
            records or those factors — no numbers are invented.
          </p>
        </div>

        {/* States */}
        {!dealId ? (
          <EmptyState
            icon={<Briefcase className="h-6 w-6" />}
            title={`No ${persona.coachEntity.toLowerCase()} selected`}
            description={`Enter a ${persona.coachEntity.toLowerCase()} id above to generate coaching.`}
          />
        ) : isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          status === 404 ? (
            <EmptyState
              icon={<Info className="h-6 w-6" />}
              title={`${persona.coachEntity} not found`}
              description={serverMessage || `No ${persona.coachEntity.toLowerCase()} exists with that id.`}
            />
          ) : status === 403 ? (
            <EmptyState
              icon={<ShieldAlert className="h-6 w-6" />}
              title="Access denied"
              description={serverMessage || `You do not have permission to view this ${persona.coachEntity.toLowerCase()}.`}
            />
          ) : (
            <EmptyState
              title="Couldn't load coaching"
              description={serverMessage || 'Check your connection or refresh to try again.'}
            />
          )
        ) : data?.ok ? (
          <div className="space-y-6">
            {/* Win-probability score */}
            <section className="space-y-2">
              <h4 className="text-data-md font-semibold text-on-surface">
                {persona.coachEntity === 'Task' ? 'Risk score' : 'Win probability'}
              </h4>
              <div className="flex items-baseline gap-2">
                <span className="text-display-sm font-semibold tabular-nums text-on-surface">
                  {data.score != null ? `${data.score}%` : '—'}
                </span>
                {data.stage && <Badge tone="info">{data.stage}</Badge>}
              </div>
            </section>

            {/* Contributing factors — verbatim from the model (Req 10.1, 14.5) */}
            <section className="space-y-2">
              <h4 className="text-data-md font-semibold text-on-surface">Contributing factors</h4>
              <Table
                columns={factorColumns}
                data={data.factors}
                rowKey={(f) => `${f.label}:${String(f.value)}`}
                empty={`No contributing factors were recorded for this ${persona.coachEntity.toLowerCase()}.`}
              />
            </section>

            {/* Grounding — every value echoes a real deal field (Req 10.2, 14.4) */}
            <section className="space-y-2">
              <h4 className="text-data-md font-semibold text-on-surface">Grounding (source data)</h4>
              <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <GroundingRow label={persona.coachEntity === 'Task' ? 'Status' : 'Stage'} value={data.grounding.stage ?? '—'} />
                
                {persona.coachEntity === 'Task' ? (
                  <>
                    <GroundingRow
                      label="Due date"
                      value={formatDate(data.grounding.dueDate ?? null)}
                      icon={<CalendarClock className="h-4 w-4 text-on-surface-variant" />}
                    />
                    <GroundingRow label="Assignee load" value={data.grounding.assigneeLoad != null ? `${data.grounding.assigneeLoad} open tasks` : '—'} />
                    <GroundingRow label="Priority" value={data.grounding.priority?.toUpperCase() ?? '—'} />
                  </>
                ) : (
                  <>
                    <GroundingRow
                      label="Value"
                      value={formatCurrency(data.grounding.value ?? 0, data.grounding.currency ?? null)}
                    />
                    <GroundingRow label="Currency" value={data.grounding.currency ?? '—'} />
                    <GroundingRow
                      label="Expected close"
                      value={formatDate(data.grounding.expectedClose ?? null)}
                      icon={<CalendarClock className="h-4 w-4 text-on-surface-variant" />}
                    />
                    <GroundingRow
                      label="Linked call activity"
                      icon={<Phone className="h-4 w-4 text-on-surface-variant" />}
                      value={
                        data.grounding.callActivity?.linked
                          ? `${formatCount(data.grounding.callActivity.callCount ?? 0)} calls`
                          : 'Unavailable (no linked lead)'
                      }
                    />
                  </>
                )}
              </dl>
            </section>

            {/* Next-best-action */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-2 text-data-md font-semibold text-on-surface">
                {data.nextBestAction.action === 'none' ? (
                  <Ban className="h-4 w-4 text-on-surface-variant" />
                ) : (
                  <Lightbulb className="h-4 w-4 text-warning-text" />
                )}
                Next best action
                <Badge tone="neutral">{data.nextBestAction.action}</Badge>
              </h4>
              <p className="text-body-md text-on-surface">{data.nextBestAction.recommendation}</p>
              {data.nextBestAction.reasons.length > 0 && (
                <ul className="list-inside list-disc space-y-1 text-body-sm text-on-surface-variant">
                  {data.nextBestAction.reasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function GroundingRow({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-outline px-3 py-2">
      <dt className="flex items-center gap-2 text-body-sm text-on-surface-variant">
        {icon}
        {label}
      </dt>
      <dd className="text-data-md tabular-nums text-on-surface">{value}</dd>
    </div>
  );
}
